import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: "11px", fontWeight: 700, color: "#999", letterSpacing: "0.7px", textTransform: "uppercase" }}>
    {children}
  </div>
);

const ICONS = {
  addSublayer: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h11M3 10h6M3 15h4" />
      <path d="M17 13v8M13 17h8" />
    </svg>
  ),
  duplicate: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  delete: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  ),
};

// Compact icon button used for the per-row layer actions. Actions stay hidden
// at rest (desktop) and reveal on row hover to keep the narrow panel clean.
const RowActionButton = ({ title, ariaLabel, onClick, size = 20, danger = false, withTooltip = true, children }) => {
  const btn = (
    <button
      type="button"
      aria-label={ariaLabel}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: `${size}px`, height: `${size}px`, padding: 0, border: "none",
        background: "transparent", color: "#9ca3af", cursor: "pointer",
        borderRadius: "5px", flexShrink: 0, transition: "color 0.12s ease, background 0.12s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = danger ? "#dc2626" : "#7c3aed";
        e.currentTarget.style.background = danger ? "#fee2e2" : "#f3e8ff";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "#9ca3af";
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </button>
  );
  return withTooltip ? <Tooltip title={title} placement="top">{btn}</Tooltip> : btn;
};

// Drag-to-reorder: a 150ms long-press OR a >3px pointer move starts the drag,
// so a quick click still selects the layer.
const LONG_PRESS_MS = 150;
const DRAG_DEAD_ZONE = 3;


const StitchSidebar = ({
  colors,
  embroidery,
  layerSelected,
  onLayerChange,
  setLayerSelected,
  onReorderLayer,
  onDeleteLayer,
  onDuplicateLayer,
  onAddSublayer,
  layerMeta,
  onColorChange,
  aiAnalysis,
  // Phone mode: parent (StitchEditor) toggles `isOpen` via the floating
  // "Layers" pill and we render as a slide-down dropdown anchored under it.
  // Desktop: `isOpen` is always true and the user collapses with the chevron.
  isPhone = false,
  isOpen = true,
  onClose,
}) => {
  const colorInteraction = useRef(null);
  const [collapsed, setCollapsed] = useState(false);
  // Which row is hovered — drives reveal of the per-row action buttons (desktop).
  const [hoveredRow, setHoveredRow] = useState(null);

  // Drag-reorder state lives only here so the rest of the editor isn't aware of it.
  const [dragState, setDragState] = useState(null);
  // dragState: { fromIndex, overIndex, ghostX, ghostY, ghostWidth, color, label }
  const rowRefs = useRef([]);
  const layerListRef = useRef(null);
  const longPressTimer = useRef(null);
  const pointerStartPos = useRef({ x: 0, y: 0 });
  const activeDragRef = useRef(null);
  const isDragging = dragState !== null;

  // Returns the insertion "gap" index in [0, n]: the slot where the dragged
  // row would land (0 = above the first row, n = below the last). Using gap
  // semantics (rather than a row index) keeps the drop indicator and the actual
  // splice in lock-step, so a drag never lands one row off from the cursor.
  const getDropIndex = useCallback((clientY) => {
    const n = colors?.length || 0;
    for (let i = 0; i < n; i++) {
      const el = rowRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (clientY < midY) return i;
    }
    return n;
  }, [colors]);

  const startDrag = useCallback((index, clientX, clientY) => {
    const row = rowRefs.current[index];
    if (!row) return;
    const rect = row.getBoundingClientRect();

    const state = {
      fromIndex: index,
      overIndex: index,
      ghostX: clientX,
      ghostY: clientY,
      ghostWidth: rect.width,
      color: colors?.[index] || "#999",
      label: `Layer ${index + 1}`,
    };
    setDragState(state);
    activeDragRef.current = state;

    const handleMove = (e) => {
      const y = e.clientY ?? e.touches?.[0]?.clientY;
      const x = e.clientX ?? e.touches?.[0]?.clientX;
      if (y == null) return;
      const over = getDropIndex(y);
      const next = { ...activeDragRef.current, ghostX: x, ghostY: y, overIndex: over };
      activeDragRef.current = next;
      setDragState(next);
    };

    const handleEnd = () => {
      const final = activeDragRef.current;
      if (final) {
        const from = final.fromIndex;
        const gap = final.overIndex; // insertion gap in [0, n]
        // Dropping into the gap directly above or below the dragged row is a no-op.
        if (gap !== from && gap !== from + 1) {
          // Convert the gap to the final post-removal index expected by the
          // splice/remap logic: dragging down shifts everything up by one.
          const target = gap > from ? gap - 1 : gap;
          onReorderLayer?.(from, target);
        }
      }
      setDragState(null);
      activeDragRef.current = null;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleEnd);
      window.removeEventListener("pointercancel", handleEnd);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleEnd);
    window.addEventListener("pointercancel", handleEnd);
  }, [colors, getDropIndex, onReorderLayer]);

  const handleRowPointerDown = useCallback((e, index) => {
    if (!onReorderLayer) return; // reorder not wired up — leave row alone
    if (e.button && e.button !== 0) return;

    pointerStartPos.current = { x: e.clientX, y: e.clientY };
    const sx = e.clientX, sy = e.clientY;

    longPressTimer.current = setTimeout(() => {
      startDrag(index, sx, sy);
    }, LONG_PRESS_MS);

    const handleEarlyMove = (moveEvt) => {
      const dx = moveEvt.clientX - pointerStartPos.current.x;
      const dy = moveEvt.clientY - pointerStartPos.current.y;
      if (Math.abs(dx) > DRAG_DEAD_ZONE || Math.abs(dy) > DRAG_DEAD_ZONE) {
        clearTimeout(longPressTimer.current);
        startDrag(index, moveEvt.clientX, moveEvt.clientY);
        window.removeEventListener("pointermove", handleEarlyMove);
        window.removeEventListener("pointerup", handleEarlyUp);
      }
    };
    const handleEarlyUp = () => {
      clearTimeout(longPressTimer.current);
      window.removeEventListener("pointermove", handleEarlyMove);
      window.removeEventListener("pointerup", handleEarlyUp);
    };
    window.addEventListener("pointermove", handleEarlyMove);
    window.addEventListener("pointerup", handleEarlyUp);
  }, [onReorderLayer, startDrag]);

  // Position of the blue drop-indicator bar inside the layer list scroll area.
  // `overIndex` is an insertion gap in [0, n]: draw the bar at the top of the
  // row that sits at that gap (or below the last row when the gap is at the end).
  const getDropIndicatorY = () => {
    if (!dragState) return null;
    const { fromIndex, overIndex: gap } = dragState;
    // Hide for no-op gaps (directly above/below the dragged row).
    if (gap === fromIndex || gap === fromIndex + 1) return null;
    const containerRect = layerListRef.current?.getBoundingClientRect();
    if (!containerRect) return null;
    const scrollTop = layerListRef.current?.scrollTop || 0;
    const n = colors?.length || 0;
    if (gap >= n) {
      const lastRef = rowRefs.current[n - 1];
      if (!lastRef) return null;
      return lastRef.getBoundingClientRect().bottom - containerRect.top + scrollTop;
    }
    const gapRef = rowRefs.current[gap];
    if (!gapRef) return null;
    return gapRef.getBoundingClientRect().top - containerRect.top + scrollTop;
  };
  const dropLineY = getDropIndicatorY();

  const panelWidth = collapsed ? 0 : 220;

  const passLabels = { auto_fill: "Fill", contour_fill: "Fill", circular_fill: "Fill", meander_fill: "Fill", satin: "Satin", running: "Running" };

  // Single source of truth for a layer/sublayer row, shared by the phone and
  // desktop panels so they never drift. `phone` tweaks behavior (always-on
  // actions, no tooltips, close-on-select) and sizing.
  const renderLayerRow = (color, i, { phone, inGroup = false }) => {
    const ls = embroidery?.getLayerSettings?.(i);
    const passes = ls?.passes || [];
    const isSelected = layerSelected === i;
    const isBeingDragged = isDragging && dragState?.fromIndex === i;
    const aiDesc = aiAnalysis?.layers?.[i]?.description;
    // Every row is a plain "Layer N" by its real stack index — grouped members
    // are still just layers, the group box is the only visual distinction.
    const displayName = `Layer ${i + 1}`;
    const showActions = phone || hoveredRow === i || isSelected;
    const btnSize = phone ? 24 : 20;
    const canDelete = onDeleteLayer && colors.length > 1;
    const actionCount = Number(Boolean(onAddSublayer)) + Number(Boolean(onDuplicateLayer)) + Number(Boolean(canDelete));
    const actionWidth = actionCount * btnSize + Math.max(0, actionCount - 1);
    const revealActions = showActions && actionCount > 0;

    return (
      <div
        key={i}
        ref={(el) => { rowRefs.current[i] = el; }}
        onPointerDown={(e) => handleRowPointerDown(e, i)}
        onClick={() => {
          if (isDragging) return;
          onLayerChange?.(i);
          if (phone) onClose?.();
        }}
        onMouseEnter={phone ? undefined : () => setHoveredRow(i)}
        onMouseLeave={phone ? undefined : () => setHoveredRow((h) => (h === i ? null : h))}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "5px 6px",
          borderRadius: "7px",
          cursor: isDragging ? "grabbing" : (onReorderLayer ? "grab" : "pointer"),
          background: isSelected ? "#f3e8ff" : "transparent",
          border: isSelected ? "1.5px solid #d8b4fe" : "1.5px solid transparent",
          transition: "background 0.12s ease, border 0.12s ease, opacity 0.12s ease",
          opacity: isBeingDragged ? 0.35 : 1,
          touchAction: onReorderLayer ? "none" : "auto",
          userSelect: "none",
        }}
      >
        {/* Drag handle dots */}
        {onReorderLayer && (
          <div
            aria-hidden
            style={{
              display: "flex", flexDirection: "column", gap: "2px", flexShrink: 0,
              opacity: 0.35, marginLeft: "-2px", marginRight: "-2px", padding: "0 2px",
            }}
          >
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "#888" }} />
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "#888" }} />
            <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: "#888" }} />
          </div>
        )}

        {/* Color swatch (doubles as a color picker) */}
        <label
          title="Change color"
          style={{ position: "relative", flexShrink: 0, cursor: "pointer", display: "inline-flex", alignItems: "center" }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            width: "16px", height: "16px", borderRadius: "4px",
            backgroundColor: color, border: "1px solid rgba(0,0,0,0.18)", flexShrink: 0,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.3)",
          }} />
          <input
            type="color"
            value={color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : "#000000"}
            onClick={() => { colorInteraction.current = Symbol("native color picker"); }}
            onBlur={() => { colorInteraction.current = null; }}
            onChange={(e) => onColorChange?.(i, e.target.value, colorInteraction.current)}
            style={{ position: "absolute", opacity: 0, width: "16px", height: "16px", inset: 0, cursor: "pointer", padding: 0, border: "none" }}
          />
        </label>

        {/* Name + AI description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span title={displayName} style={{ fontSize: "12px", fontWeight: isSelected ? 600 : 500, color: "#333", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {displayName}
          </span>
          {aiDesc && !inGroup && (
            <span style={{ fontSize: "10px", color: "#888", display: "block", lineHeight: "1.3", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {aiDesc}
            </span>
          )}
        </div>

        {/* Desktop badges and actions share a fixed slot so hovering cannot
            squeeze the name or change the row height. Phone layout stays inline. */}
        <div style={{ position: "relative", flexShrink: 0, width: phone ? undefined : `${Math.max(64, actionWidth)}px`, minHeight: `${btnSize}px`, display: phone ? "contents" : "flex", alignItems: "center", justifyContent: "flex-end" }}>
        {/* Pass-type chips */}
        {passes.length > 0 && (
          <div style={{ display: "flex", gap: "2px", flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: phone ? "55%" : "100%", visibility: !phone && revealActions ? "hidden" : "visible" }}>
            {passes.map((pass, pi) => {
              const active = pi === (ls?.activePassIndex || 0);
              const chip = (
                <span style={{
                  fontSize: "10px", padding: "1px 4px", borderRadius: "3px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  background: active ? "#fce7f3" : "transparent",
                  color: active ? "#d946ef" : "#aaa",
                }}>
                  {passLabels[pass.stitchType] || passLabels[pass.fill_method] || "Fill"}
                </span>
              );
              return phone
                ? <span key={pass.id || pi}>{chip}</span>
                : <Tooltip key={pass.id || pi} title={`Pass ${pi + 1}: ${pass.stitchType || "Fill"}`} placement="top" open={revealActions ? false : undefined}>{chip}</Tooltip>;
            })}
          </div>
        )}

        {/* Row actions — revealed on hover (desktop) / always (phone) */}
        {revealActions && (
          <div style={{ display: "flex", alignItems: "center", gap: "1px", flexShrink: 0, ...(phone ? {} : { position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }) }}>
            {onAddSublayer && (
              <RowActionButton title="Add sublayer" ariaLabel="Add sublayer" size={btnSize} withTooltip={!phone} onClick={() => onAddSublayer(i)}>
                {ICONS.addSublayer}
              </RowActionButton>
            )}
            {onDuplicateLayer && (
              <RowActionButton title="Duplicate layer" ariaLabel="Duplicate layer" size={btnSize} withTooltip={!phone} onClick={() => onDuplicateLayer(i)}>
                {ICONS.duplicate}
              </RowActionButton>
            )}
            {canDelete && (
              <RowActionButton title="Delete layer" ariaLabel="Delete layer" size={btnSize} danger withTooltip={!phone} onClick={() => onDeleteLayer(i)}>
                {ICONS.delete}
              </RowActionButton>
            )}
          </div>
        )}
        </div>
      </div>
    );
  };

  // Renders the full list, wrapping each color group's members (parent + its
  // sublayers) in a soft container so they read as one equal-level collection
  // rather than a parent/child hierarchy.
  const renderLayerList = (phone) => {
    const n = colors?.length || 0;
    const items = [];
    let i = 0;
    while (i < n) {
      const meta = layerMeta?.[i] || {};
      if (meta.hasGroup && meta.isGroupParent) {
        const members = [];
        let j = i;
        while (j < n && (layerMeta?.[j]?.groupId === meta.groupId)) { members.push(j); j++; }
        items.push(
          <div
            key={`group-${meta.groupId}`}
            style={{
              display: "flex", flexDirection: "column", gap: "2px",
              padding: "3px", borderRadius: "10px",
              background: "rgba(124,58,237,0.05)",
              border: "1px solid rgba(124,58,237,0.13)",
            }}
          >
            {members.map((idx) => renderLayerRow(colors[idx], idx, { phone, inGroup: true }))}
          </div>
        );
        i = j;
      } else {
        items.push(renderLayerRow(colors[i], i, { phone, inGroup: false }));
        i++;
      }
    }
    return items;
  };

  // Phone mode: slide-down dropdown anchored under the floating Layers pill.
  // Bypass the desktop right-side container entirely so we don't fight its
  // vertical centering or chevron toggle.
  if (isPhone) {
    return (
      <>
        <div
          style={{
            position: "absolute",
            // Anchored just below the Layers pill (which sits below the
            // top-right zoom pill). Bumped down so the drawer doesn't poke
            // into either of them.
            top: "86px",
            right: "12px",
            zIndex: 110,
            width: "min(190px, calc(100vw - 24px))",
            maxHeight: isOpen ? "calc(100dvh - 200px)" : "0px",
            opacity: isOpen ? 1 : 0,
            overflow: "hidden",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            borderRadius: "12px",
            boxShadow: isOpen
              ? "0 8px 32px rgba(0,0,0,0.15), 0 2px 8px rgba(0,0,0,0.08)"
              : "none",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            transition: "max-height 0.3s ease, opacity 0.2s ease",
            pointerEvents: isOpen ? "auto" : "none",
            display: "flex",
            flexDirection: "column",
            fontFamily: "Clash Grotesk, sans-serif",
          }}
        >
          <div style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px 9px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            minHeight: 0,
          }}>
            {/* Layers */}
            <div>
              <SectionLabel>Stitch Colors</SectionLabel>
              <div
                ref={layerListRef}
                style={{ display: "flex", flexDirection: "column", gap: "3px", marginTop: "6px", position: "relative" }}
              >
                {dropLineY != null && (
                  <div style={{
                    position: "absolute",
                    left: 0, right: 0,
                    top: `${dropLineY}px`,
                    height: "3px",
                    borderRadius: "2px",
                    backgroundColor: "#a855f7",
                    boxShadow: "0 0 6px rgba(168,85,247,0.6)",
                    transform: "translateY(-1.5px)",
                    zIndex: 10,
                    pointerEvents: "none",
                  }} />
                )}
                {renderLayerList(true)}
              </div>
            </div>

          </div>
        </div>

        {/* Floating ghost row when dragging — same as desktop. */}
        {isDragging && typeof document !== "undefined" && createPortal(
          <div
            style={{
              position: "fixed",
              top: 0, left: 0,
              width: `${dragState.ghostWidth}px`,
              transform: `translate(${dragState.ghostX - dragState.ghostWidth / 2}px, ${dragState.ghostY - 18}px) scale(1.04)`,
              zIndex: 99999,
              pointerEvents: "none",
              opacity: 0.95,
              transition: "transform 0.05s linear",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              padding: "6px 10px",
              background: "#fff",
              border: "1.5px solid #a855f7",
              borderRadius: "8px",
              boxShadow: "0 12px 28px rgba(168,85,247,0.25), 0 4px 10px rgba(0,0,0,0.12)",
              fontFamily: "Clash Grotesk, sans-serif",
            }}>
              <div style={{
                width: "16px", height: "16px", borderRadius: "4px",
                backgroundColor: dragState.color,
                border: "1px solid rgba(0,0,0,0.1)",
                flexShrink: 0,
              }} />
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#333", whiteSpace: "nowrap" }}>
                {dragState.label}
              </span>
            </div>
          </div>,
          document.body,
        )}
      </>
    );
  }

  return (
    <div style={{
      position: "absolute",
      right: "8px",
      top: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      zIndex: 50,
      pointerEvents: "none",
    }}>
      {/* Collapse toggle chevron */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          pointerEvents: "auto",
          width: "20px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.95)",
          border: "none",
          borderRadius: "6px 0 0 6px",
          cursor: "pointer",
          fontSize: "11px",
          color: "#888",
          boxShadow: "-2px 0 8px rgba(0,0,0,0.06)",
          flexShrink: 0,
        }}
      >
        {collapsed ? "‹" : "›"}
      </button>

      {/* Panel */}
      <div style={{
        width: `${panelWidth}px`,
        maxHeight: "calc(100% - 24px)",
        background: "rgba(255,255,255,0.97)",
        borderRadius: "12px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Clash Grotesk, sans-serif",
        overflow: "hidden",
        transition: "width 0.2s ease",
        pointerEvents: collapsed ? "none" : "auto",
        opacity: collapsed ? 0 : 1,
      }}>
        {/* Scrollable content */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "8px 9px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}>
          {/* Layers */}
          <div>
            <SectionLabel>Stitch Colors</SectionLabel>
            <div
              ref={layerListRef}
              style={{ display: "flex", flexDirection: "column", gap: "3px", marginTop: "6px", position: "relative" }}
            >
              {/* Drop-indicator bar — only visible while dragging */}
              {dropLineY != null && (
                <div style={{
                  position: "absolute",
                  left: 0, right: 0,
                  top: `${dropLineY}px`,
                  height: "3px",
                  borderRadius: "2px",
                  backgroundColor: "#a855f7",
                  boxShadow: "0 0 6px rgba(168,85,247,0.6)",
                  transform: "translateY(-1.5px)",
                  zIndex: 10,
                  pointerEvents: "none",
                }} />
              )}
              {renderLayerList(false)}
            </div>
          </div>

        </div>

      </div>

      {/* Floating ghost row that follows the cursor while dragging.
          Portalled to <body> so the panel's blur/scroll/clip don't constrain it. */}
      {isDragging && typeof document !== "undefined" && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: `${dragState.ghostWidth}px`,
            transform: `translate(${dragState.ghostX - dragState.ghostWidth / 2}px, ${dragState.ghostY - 18}px) scale(1.04)`,
            zIndex: 99999,
            pointerEvents: "none",
            opacity: 0.95,
            transition: "transform 0.05s linear",
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "6px 10px",
            background: "#fff",
            border: "1.5px solid #a855f7",
            borderRadius: "8px",
            boxShadow: "0 12px 28px rgba(168,85,247,0.25), 0 4px 10px rgba(0,0,0,0.12)",
            fontFamily: "Clash Grotesk, sans-serif",
          }}>
            <div style={{
              width: "16px", height: "16px", borderRadius: "4px",
              backgroundColor: dragState.color,
              border: "1px solid rgba(0,0,0,0.1)",
              flexShrink: 0,
            }} />
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#333", whiteSpace: "nowrap" }}>
              {dragState.label}
            </span>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default StitchSidebar;
